import * as React from 'react';
import * as Styled from './styled';
import { IconButton, Icon, Input, Paper, Tooltip, InputAdornment } from '@material-ui/core';
import { Add, Clear, Delete, Language, Search } from '@material-ui/icons';
import NarrativeStore from 'store/narrative.store';
import { inject, observer } from 'mobx-react';
import { RootStore } from 'store/root.store';
import { Route } from 'react-router';
import NarrativeForm from './NarrativeForm';
import { AutoSizer, List, ListRowProps } from 'react-virtualized';
import { theme } from 'common/theme';
import { TKConsumer } from 'common/TKProvider';
import { TimeKeeperAssignment } from '../../api/types/types';
import { withTranslation } from 'react-i18next';
import { LoadingDiv } from 'containers/Home/Home';
import { handleEscapeKey } from 'util/hotKeys';

interface Props {
    narrativeStore?: NarrativeStore;
    location: {
        pathname: string;
    };
    match: {
        url: string;
    };
    // tslint:disable-next-line:no-any
    t: any;
}

interface GGProps extends ListRowProps {
    handleClick: (id: number) => void;
    narrativeStore?: NarrativeStore;
    match: {
        url: string;
    };
    // tslint:disable-next-line:no-any
    t: any;
}

@inject((allStores: { rootStore: RootStore }) => {
    let rootStore = allStores.rootStore;
    return {
        narrativeStore: rootStore.narrativeStore
    };
})
@observer
class ListRow extends React.Component<GGProps> {

    render() {
        const { index, key, style, handleClick, narrativeStore, t } = this.props;
        const {
            narratives,
            selectedNarrative,
            deleteNarrative,
            rootStore
        } = narrativeStore!;

        const curNarrative = narratives[index];
        const selected = selectedNarrative && curNarrative && selectedNarrative.id === curNarrative.id;

        return (
            <div
                key={key}
                style={{ ...style }}
            >
                <Styled.ListItemContainer
                    selected={selected}
                    aria-selected={selected}
                    onClick={() => handleClick(curNarrative.id!)}
                    style={{
                        width: '100%',
                        height: '100%'
                    }}
                >
                    {curNarrative ? (
                        <Styled.ListItem selected={selected} style={{height: style.height}}>
                            <div style={{ minWidth: '20px' }}>
                                <Styled.Key aria-label={t('narrative_list.code.aria_label', { key: curNarrative.key })} >{curNarrative.key}</Styled.Key>
                                <Styled.Replacement aria-label={t('narrative_list.narrative.aria_label', { key: curNarrative.replacement })}>
                                    {curNarrative.replacement}
                                </Styled.Replacement>
                            </div>
                            <TKConsumer>
                                { (tk: TimeKeeperAssignment) =>
                            <div>
                                {!curNarrative.global ? tk.writable &&
                                    <Tooltip title={t('delete', { ns: 'common' })} aria-hidden="true">
                                        <IconButton
                                            onClick={() => {
                                                rootStore.appStore.setPreviousActiveElement();
                                                deleteNarrative(curNarrative.id!);
                                            }}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    </Tooltip>
                                    :
                                    <Tooltip title={t('list.item.icon.global')}>
                                        <Icon style={{ margin: '12px' }}>
                                            <Language style={{ color: theme.primary.main }}/>
                                        </Icon>
                                    </Tooltip>
                                }
                            </div>}
                            </TKConsumer>
                        </Styled.ListItem>
                    ) : (
                        <Styled.ListItem data-no-narrative={true} />
                    )}
                </Styled.ListItemContainer>
            </div>
        );
    }
}

@inject((allStores: { rootStore: RootStore }) => {
    let rootStore = allStores.rootStore;
    return {
        narrativeStore: rootStore.narrativeStore
    };
})
@observer
class NarrativesDesktop extends React.Component<Props> {
    isFetching = true;
    listRef = React.createRef<List>();
    formCodeFieldRef = React.createRef<HTMLInputElement>();
    rightPanelRef = React.createRef<HTMLElement>();

    componentDidMount() {
        const narrativeStore = this.props.narrativeStore!;
        narrativeStore.fetchAllNarratives(); // load all narratives to check aginst when adding new
        narrativeStore.reset();
        narrativeStore.fetchMoreAvailNarratives();
        this.isFetching = false;
        if (narrativeStore.selectedNarrative || location.hash === '#/narratives') {
            narrativeStore.newNarrative();
        }
    }

    componentWillUnmount() {
        this.props.narrativeStore!.resetSearchText();
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        const { location, narrativeStore } = this.props;
        if (location.pathname === '/narratives' && location.pathname !== prevProps.location.pathname) {
            narrativeStore!.newNarrative();
        }
    }

    fetchMoreitems = async (clientHeight: number, scrollHeight: number, scrollTop: number) => {
        let bottom = scrollHeight - scrollTop - clientHeight;
        if (bottom < 400 && !this.isFetching) {
            this.isFetching = true;
            await this.props.narrativeStore!.fetchMoreAvailNarratives();
            this.isFetching = false;
        }
    }

    handleOnChange = (input: string) => {
        this.listRef.current!.scrollToRow(0);
        this.props.narrativeStore!.onSearchChange(input);
    }

    handleItemClick = async (id: number) => {
        await this.props.narrativeStore!.changeSelectedNarrative(id);
        this.props.narrativeStore!.rootStore.appStore.setPreviousActiveElement();
        const inputField = this.formCodeFieldRef.current;
        if (inputField && !inputField.disabled) {
            inputField.focus();
        }
    }

    render() {
        const { t, match, narrativeStore } = this.props;
        const {
            narratives,
            newNarrative,
            searchText,
            loading,
            rootStore
        } = narrativeStore!;

        return (
            <Styled.Container>
                <Styled.Header>
                    <Styled.SearchContainer>
                        <Search
                            style={{ marginRight: '6px' }}
                        />
                        <Input
                            placeholder={t('header.field.search.placeholder')}
                            fullWidth={true}
                            style={{ maxWidth: '60%' }}
                            value={searchText}
                            onChange={e => this.handleOnChange(e.target.value)}
                            inputProps={{
                                'data-auto-focus': true
                            }}
                            endAdornment={
                                searchText ? (
                                    <InputAdornment position={'end'} onClick={() => this.handleOnChange('')}>
                                        <Clear style={{ width: 20, cursor: 'pointer' }} />
                                    </InputAdornment>
                                ) : null
                            }
                        />
                    </Styled.SearchContainer>

                    <Styled.Title variant="h6">
                        {t('header.title')}
                    </Styled.Title>
                    <TKConsumer>
                        {(tk: TimeKeeperAssignment) =>
                            <div style={{ flex: '1' }}>
                                {tk.writable &&
                                    <Tooltip title={t('header.action.add')}>
                                        <Styled.New
                                            onClick={(e) => {
                                                rootStore.appStore.resetPreviousActiveElements();
                                                rootStore.appStore.setPreviousActiveElement();
                                                newNarrative();
                                                if (this.formCodeFieldRef.current) {
                                                    this.formCodeFieldRef.current.focus();
                                                }
                                            }}
                                        >
                                            <Add />
                                        </Styled.New>
                                    </Tooltip>
                                }
                            </div>}
                    </TKConsumer>
                </Styled.Header>

                <Styled.ContentContainer>
                    <Styled.ListContainer square={true}>
                        <AutoSizer>
                            {({ width, height }) => {
                                return (<List
                                    ref={this.listRef}
                                    height={height}
                                    rowHeight={80}
                                    rowCount={narratives.length}
                                    width={width}
                                    onScroll={({ clientHeight, scrollHeight, scrollTop }: 
                                                    {clientHeight: number; scrollHeight: number; scrollTop: number}) =>
                                        this.fetchMoreitems(clientHeight, scrollHeight, scrollTop)}
                                    rowRenderer={(props) => {
                                        return (
                                            <ListRow
                                                handleClick={this.handleItemClick}
                                                match={match}
                                                t={t}
                                                {...props}
                                            />
                                        );
                                    }}
                                />);
                            }}
                        </AutoSizer>
                    </Styled.ListContainer>
                    <Styled.FormContainer innerRef={this.rightPanelRef} onKeyDown={e => handleEscapeKey(e, this.rightPanelRef.current)}>
                        <Paper style={{ padding: '10px' }}>
                            <Route
                                path={`${match.url}/:id`}
                                render={(props) => <NarrativeForm {...props} formCodeFieldRef={this.formCodeFieldRef}/>}
                            />
                        </Paper>
                    </Styled.FormContainer>
                </Styled.ContentContainer>
                {loading && LoadingDiv()}
            </Styled.Container>
        );
    }
}

export default withTranslation(['narratives', 'common'])(NarrativesDesktop);